import React, { useEffect, useRef } from "react";
import { gsap } from "../../lib/gsap";
import profile from "../../data/profile";

export default function WorkExperienceSection({ subSlide = 0 }) {
  const sectionRef = useRef(null);
  const cardRefs = useRef([]);

  // Cross-fade between the two experience cards (same pattern as Education)
  useEffect(() => {
    const cards = cardRefs.current.filter(Boolean);
    cards.forEach((c, i) => {
      const isActive = i === subSlide;
      gsap.to(c, {
        opacity: isActive ? 1 : 0,
        y: isActive ? 0 : (i < subSlide ? -40 : 40),
        pointerEvents: isActive ? "auto" : "none",
        duration: 0.6,
        ease: "power3.out",
      });
    });
  }, [subSlide]);

  useEffect(() => {
    // Ensure only slide 0 is visible on mount
    const cards = cardRefs.current.filter(Boolean);
    cards.forEach((c, i) => {
      if (!c) return;
      c.style.opacity = i === 0 ? "1" : "0";
      c.style.pointerEvents = i === 0 ? "auto" : "none";
    });
  }, []);

  return (
    <section ref={sectionRef} className="snap-section" data-testid="experience-section"
      style={{ background: "var(--ink)", color: "var(--paper)" }}>
      <div className="absolute inset-0 pointer-events-none opacity-[0.05]"
        style={{ backgroundImage: "linear-gradient(var(--paper) 1px, transparent 1px)", backgroundSize: "100% 60px" }} />

      <div className="relative h-full w-full px-6 md:px-16 pt-16 md:pt-24 md:[@media(max-height:820px)]:pt-16 pb-4 md:pb-10 md:[@media(max-height:820px)]:pb-6 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-end mb-3 md:mb-6 md:[@media(max-height:820px)]:mb-4">
          <div>
            <p className="font-mono text-[9px] md:text-[10px] tracking-[0.35em] uppercase text-[var(--accent)]">Experience</p>
            <h2 className="mt-1 md:mt-2 font-serif text-[clamp(1.5rem,min(7vw,9.5vh),4.5rem)] leading-[0.9] tracking-tight">
              Where I&apos;ve <span className="italic text-[var(--accent)]">worked.</span>
            </h2>
          </div>
          <span className="font-mono text-[9px] md:text-[10px] tracking-[0.25em] uppercase text-[color:rgba(245,242,236,0.55)]">
            0{subSlide + 1} / 0{profile.experience.length}
          </span>
        </div>

        {/* Sub-slide progress bar */}
        <div className="flex items-center gap-3 mb-3 md:mb-6 md:[@media(max-height:820px)]:mb-4">
          {profile.experience.map((_, i) => (
            <div key={i} className="flex-1 h-[2px] bg-[rgba(245,242,236,0.15)] overflow-hidden rounded-full">
              <div className="h-full bg-[var(--accent)] transition-all duration-500"
                   style={{ width: i <= subSlide ? "100%" : "0%" }} />
            </div>
          ))}
          <span className="hidden md:inline font-mono text-[10px] tracking-[0.25em] uppercase text-[color:rgba(245,242,236,0.55)] whitespace-nowrap">
            {subSlide === profile.experience.length - 1 ? "Scroll for Education" : "Scroll for next role"}
          </span>
        </div>

        {/* Cross-fade card stack */}
        <div className="relative flex-1 max-w-4xl mx-auto w-full">
          {profile.experience.map((e, i) => (
            <div key={e.id} ref={el => (cardRefs.current[i] = el)}
              data-testid={`experience-card-${i}`}
              className="absolute inset-0 flex items-start">
              <div className="w-full rounded-2xl p-4 md:p-8 md:[@media(max-height:820px)]:p-5 border border-[rgba(245,242,236,0.14)] bg-white/[0.04] backdrop-blur">
                <div className="flex flex-wrap items-center gap-1.5 md:gap-2 mb-2 md:mb-4 md:[@media(max-height:820px)]:mb-2 text-[9px] md:text-[10px] font-mono uppercase tracking-[0.22em] text-[color:rgba(245,242,236,0.6)]">
                  <span className="w-7 h-7 md:w-10 md:h-10 md:[@media(max-height:820px)]:w-8 md:[@media(max-height:820px)]:h-8 rounded-full border-2 border-[var(--accent)] text-[var(--accent)] flex items-center justify-center font-medium">
                    0{i + 1}
                  </span>
                  <span className="ml-1.5 md:ml-3">{e.period} to {e.periodEnd}</span>
                  <span className="text-[var(--accent)]">·</span>
                  <span>{e.type}</span>
                </div>
                <h3 className="font-serif text-xl md:text-4xl md:[@media(max-height:820px)]:text-3xl leading-tight">{e.company}</h3>
                <p className="mt-1 md:mt-2 text-[13px] md:text-lg md:[@media(max-height:820px)]:text-base text-[var(--accent)] italic">{e.role}</p>
                <ul className="mt-2 md:mt-6 md:[@media(max-height:820px)]:mt-3 space-y-1.5 md:space-y-3 md:[@media(max-height:820px)]:space-y-2">
                  {e.bullets.map((b, bi) => (
                    <li key={bi} className="text-[12px] md:text-[15px] md:[@media(max-height:820px)]:text-[13px] leading-snug md:leading-relaxed md:[@media(max-height:820px)]:leading-snug text-[color:rgba(245,242,236,0.8)] flex gap-2 md:gap-3">
                      <span className="text-[var(--accent)] flex-shrink-0 mt-0.5 md:mt-1">›</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-2 md:mt-6 md:[@media(max-height:820px)]:mt-3 flex flex-wrap gap-1 md:gap-1.5">
                  {e.tech.map(t => (
                    <span key={t} className="px-2 py-0.5 md:px-2.5 md:py-1 rounded-full text-[9px] md:text-[10px] font-mono uppercase tracking-[0.15em] border border-[rgba(245,242,236,0.15)] text-[color:rgba(245,242,236,0.7)]">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
